import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useParams, useLocation, Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { SearchableSelect } from "@/lib/picker-search";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FileText, Plus, Search, ChevronRight, Archive, Users, Package, Megaphone } from "lucide-react";
import type { LandingPage, LandingPageTemplate, Campaign, Persona, Product } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const statusColors: Record<string, string> = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  review: "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300",
  published: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
};

export default function LandingPagesPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<LandingPage | null>(null);

  const [newTitle, setNewTitle] = useState("");
  const [importMode, setImportMode] = useState<"fresh" | "import">("fresh");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");

  const { data: landingPages, isLoading } = useQuery<LandingPage[]>({
    queryKey: ["/api/brands", brandId, "landing-pages"],
    queryFn: async () => {
      const res = await fetch(`/api/brands/${brandId}/landing-pages`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch landing pages");
      return res.json();
    },
    enabled: !!brandId,
  });

  const { data: campaigns } = useQuery<Campaign[]>({
    queryKey: [`/api/brands/${brandId}/campaigns`],
    enabled: !!brandId,
  });

  const { data: personas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    enabled: !!brandId,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: [`/api/brands/${brandId}/products`],
    enabled: !!brandId,
  });

  const { data: templates } = useQuery<LandingPageTemplate[]>({
    queryKey: ["/api/landing-page-templates"],
  });

  const personaMap = new Map(personas?.map(p => [p.id, p]) || []);
  const productMap = new Map(products?.map(p => [p.id, p]) || []);

  const defaultTemplate = templates?.find(t => t.isDefault) ?? templates?.[0];

  useEffect(() => {
    if (createOpen && !selectedTemplateId && defaultTemplate?.id) {
      setSelectedTemplateId(defaultTemplate.id);
    }
  }, [createOpen, defaultTemplate?.id]);

  const createMutation = useMutation({
    mutationFn: async () => {
      const slug = newTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || `page-${Date.now()}`;
      const res = await apiRequest("POST", `/api/brands/${brandId}/landing-pages`, {
        title: newTitle,
        slug,
        status: "draft",
      });
      const page = await res.json();
      if (importMode === "import" && selectedCampaignId) {
        await apiRequest("POST", `/api/landing-pages/${page.id}/import-campaign`, { campaignId: selectedCampaignId });
      }
      const templateId = selectedTemplateId || defaultTemplate?.id;
      if (templateId) {
        const tmpl = templates?.find(t => t.id === templateId);
        if (tmpl) {
          const tmplSections = (tmpl.sections as any[]).map((s: any) => ({
            landingPageId: page.id,
            sectionType: s.sectionType,
            order: s.order,
            enabled: s.enabled,
            variant: s.variant || "default",
          }));
          if (tmplSections.length > 0) {
            await apiRequest("POST", `/api/landing-pages/${page.id}/sections`, tmplSections);
          }
        }
      }
      return page;
    },
    onSuccess: (page) => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "landing-pages"] });
      setCreateOpen(false);
      setNewTitle("");
      setImportMode("fresh");
      setSelectedCampaignId("");
      setSelectedTemplateId("");
      navigate(`/brands/${brandId}/landing-pages/${page.id}`);
    },
    onError: (e: any) => {
      toast({ title: "Failed to create landing page", description: e.message, variant: "destructive" });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("POST", `/api/landing-pages/${id}/archive`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "landing-pages"] });
      toast({ title: "Landing page archived" });
      setArchiveTarget(null);
    },
    onError: (e: any) => {
      toast({ title: "Failed to archive", description: e.message, variant: "destructive" });
    },
  });

  const filtered = landingPages?.filter(p => {
    if (search && !p.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  }) ?? [];

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-landing-pages-title">Landing Pages</h1>
          <p className="text-sm text-muted-foreground mt-1">Build and manage landing pages for your campaigns</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="button-new-landing-page">
          <Plus className="h-4 w-4 mr-2" />
          New Landing Page
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search landing pages..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9"
          data-testid="input-search-landing-pages"
        />
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-border/50">
          <CardContent className="flex flex-col items-center justify-center py-20">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
              <FileText className="h-7 w-7 text-primary" />
            </div>
            <h3 className="font-semibold tracking-tight mb-1.5 text-lg">No landing pages yet</h3>
            <p className="text-sm text-muted-foreground mb-1 text-center max-w-sm">
              Create landing pages with sections, offer data, and AI-drafted copy for your campaigns.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="grid-landing-pages">
          {filtered.map(page => {
            const persona = page.personaId ? personaMap.get(page.personaId) : null;
            const product = page.productId ? productMap.get(page.productId) : null;
            return (
              <Card
                key={page.id}
                className="border-border/40 hover:border-primary/30 cursor-pointer transition-all group"
                onClick={() => navigate(`/brands/${brandId}/landing-pages/${page.id}`)}
                data-testid={`card-landing-page-${page.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3.5 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <FileText className="h-5 w-5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold leading-snug line-clamp-1" data-testid={`text-lp-name-${page.id}`}>
                            {page.title}
                          </p>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium shrink-0 ${statusColors[page.status] || statusColors.draft}`}>
                            {page.status}
                          </span>
                        </div>
                        {page.narrativeAngle && (
                          <p className="text-xs text-muted-foreground line-clamp-1 mb-2">{page.narrativeAngle}</p>
                        )}
                        <div className="flex items-center gap-3 flex-wrap">
                          {persona && (
                            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                              <Users className="h-3 w-3 text-primary/60" />
                              <span className="truncate max-w-[120px]">{persona.name}</span>
                            </div>
                          )}
                          {product && (
                            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                              <Package className="h-3 w-3 text-blue-500/60" />
                              <span className="truncate max-w-[120px]">{product.name}</span>
                            </div>
                          )}
                          {page.updatedAt && (
                            <span className="text-[11px] text-muted-foreground/60">
                              {new Date(page.updatedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground/30 opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-all"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setArchiveTarget(page);
                        }}
                        data-testid={`button-archive-${page.id}`}
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </button>
                      <ChevronRight className="h-4 w-4 text-muted-foreground/30 group-hover:text-muted-foreground/60 transition-colors" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="dialog-new-landing-page">
          <DialogHeader>
            <DialogTitle>New Landing Page</DialogTitle>
            <DialogDescription>Create a new landing page for this brand</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                placeholder="e.g. Summer Sale Hero Page"
                value={newTitle}
                onChange={e => setNewTitle(e.target.value)}
                data-testid="input-new-title"
              />
            </div>
            {templates && templates.length > 0 && (
              <div className="space-y-2">
                <Label>Template</Label>
                <SearchableSelect
                  value={selectedTemplateId || undefined as any}
                  onValueChange={setSelectedTemplateId}
                  placeholder="Select a template..."
                  testId="select-template"
                  options={templates.map(t => ({
                    value: t.id,
                    label: `${t.name}${t.isDefault ? " (default)" : ""}`,
                  }))}
                />
              </div>
            )}
            <div className="space-y-3">
              <Label>Campaign</Label>
              <RadioGroup value={importMode} onValueChange={(v) => setImportMode(v as "fresh" | "import")} className="space-y-2">
                <div className="flex items-center space-x-3 rounded-lg border border-border/40 p-3 cursor-pointer hover:bg-muted/20" onClick={() => setImportMode("fresh")}>
                  <RadioGroupItem value="fresh" id="fresh" />
                  <Label htmlFor="fresh" className="cursor-pointer text-sm font-medium">Start Fresh</Label>
                </div>
                <div className="flex items-center space-x-3 rounded-lg border border-border/40 p-3 cursor-pointer hover:bg-muted/20" onClick={() => setImportMode("import")}>
                  <RadioGroupItem value="import" id="import" />
                  <Label htmlFor="import" className="cursor-pointer text-sm font-medium">Import from Campaign</Label>
                </div>
              </RadioGroup>
              {importMode === "import" && (
                <SearchableSelect
                  value={selectedCampaignId}
                  onValueChange={setSelectedCampaignId}
                  placeholder="Select a campaign..."
                  testId="select-campaign"
                  options={(campaigns ?? []).map(c => ({
                    value: c.id,
                    label: c.name,
                    node: (
                      <div className="flex items-center gap-2">
                        <Megaphone className="h-3 w-3" />
                        {c.name}
                      </div>
                    ),
                  }))}
                />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!newTitle.trim() || createMutation.isPending || (importMode === "import" && !selectedCampaignId)}
              data-testid="button-create-landing-page"
            >
              {createMutation.isPending ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveTarget} onOpenChange={open => !open && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive landing page?</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive "{archiveTarget?.title}". You can restore it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => archiveTarget && archiveMutation.mutate(archiveTarget.id)}
              data-testid="button-confirm-archive"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

